import formatNumber from 'helpers/formatNumber';

export const SELECTED_LAYER_TYPE = 'selected';
export const FOCUSED_LAYER_TYPE = 'focused';
export const CUSTOM_LAYER_TYPE = 'custom';

export const conditionFieldWithCropZones = [
  'all',
  ['==', ['get', '$landType'], 'field'],
  ['>', ['length', ['get', 'cropzones']], 0]
];
export const conditionLayerSelected = [
  '==',
  ['get', '$layer'],
  SELECTED_LAYER_TYPE
];
export const conditionLayerCustom = [
  '==',
  ['get', '$layer'],
  CUSTOM_LAYER_TYPE
];

// Only the export flow writes sort keys onto features, and mapbox-gl logs a
// failed-expression warning on every tile parse when the property is missing.
// It then sorts the feature as 0 anyway, so the fallback changes nothing on
// screen.
export const symbolSortKey = property => ['coalesce', ['get', property], 0];

export const symbolLayoutTemplate = {
  'text-anchor': 'center',
  'text-justify': 'center',
  'text-variable-anchor': ['center'],
  'text-radial-offset': 0,
  'text-size': 10,
  'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
  'text-allow-overlap': true,
  'symbol-sort-key': symbolSortKey('sortKey')
};

export const customSymbolLayout = {
  ...symbolLayoutTemplate,
  'text-field': [
    'case',
    conditionFieldWithCropZones,
    '',
    ['get', 'customLabel']
  ],
  'symbol-sort-key': symbolSortKey('symbolSortKey')
};

export const legendSymbolLayout = {
  ...symbolLayoutTemplate,
  'text-field': ['get', 'labelLegendKey'],
  'symbol-sort-key': symbolSortKey('symbolSortKey')
};

export const customSymbolPaint = {
  'text-color': 'white',
  'text-halo-color': 'black',
  'text-halo-width': 1
};

export const sortMapLayers = mapRef => {
  let layers = mapRef.current?.getStyle()?.layers;
  if (!layers?.length) return;

  layers = layers.filter(layer => layer.id.startsWith('geojson-'));
  layers.forEach(layer => {
    mapRef.current.removeLayer(layer.id);
  });

  const dynamicLayers = [];
  const geojsonLayers = [];
  const geojsonSymbolLayers = [];

  layers.forEach(layer => {
    if (layer.id.startsWith('geojson-dynamic-layer')) {
      dynamicLayers.push(layer);
    } else if (/geojson-\d+-symbol.*/.test(layer.id)) {
      geojsonSymbolLayers.push(layer);
    } else {
      geojsonLayers.push(layer);
    }
  });

  geojsonSymbolLayers.sort((a, b) => {
    const getNumber = id => {
      const num = Number.parseInt(id.split('-')[1], 10);
      if (Number.isNaN(num)) return Infinity;
      return num;
    };
    return getNumber(b.id) - getNumber(a.id);
  });

  const sortedLayers = [
    ...dynamicLayers,
    ...geojsonLayers,
    ...geojsonSymbolLayers
  ];

  sortedLayers.forEach(layer => {
    mapRef.current.addLayer(layer);
  });
};

const isOurLayer = layer => layer.id.startsWith('geojson-');
const isLabelLayer = layer => isOurLayer(layer) && layer.type === 'symbol';
const isShapeLayer = layer => isOurLayer(layer) && layer.type !== 'symbol';
const isDrawLayer = layer => layer.id.startsWith('gl-draw');

// A layer mounts on top of everything already on the map, and the focused
// fill remounts on every selection, so it ended up covering the field names.
// Moves the label layers above every shape layer, keeping their order and
// staying below the draw tools. A no-op once they are there, so it is safe to
// run from the styledata event that the move itself fires.
export const raiseLabelLayers = map => {
  const layers = map?.getStyle()?.layers ?? [];
  const labels = layers.filter(isLabelLayer);
  const lastShape = layers.map(isShapeLayer).lastIndexOf(true);
  if (!labels.length || lastShape === -1) return;

  const anchorIndex = layers.findIndex(
    (layer, index) => index > lastShape && isDrawLayer(layer)
  );
  const end = anchorIndex === -1 ? layers.length : anchorIndex;
  const inPlace = layers
    .slice(end - labels.length, end)
    .every((layer, index) => layer.id === labels[index].id);
  if (!inPlace) {
    const anchor = anchorIndex === -1 ? undefined : layers[anchorIndex].id;
    labels.forEach(label => map.moveLayer(label.id, anchor));
  }

  // A shape group that mounts after the draw control lands above it and hides
  // the vertices and handles; the draw layers go back on top, in their order.
  const drawBelowShapes = layers.some(
    (layer, index) => index < lastShape && isDrawLayer(layer)
  );
  if (drawBelowShapes) {
    layers.filter(isDrawLayer).forEach(layer => map.moveLayer(layer.id));
  }
};

export const isFeatureSelected = (fieldsToMassiveAssign, feature) =>
  fieldsToMassiveAssign.find(
    field =>
      field.id === feature.properties.id ||
      field.cropzones
        .map(cropzone => cropzone.id)
        .find(id => id === feature.properties.id)
  );

export const hasCustomLabel = feature =>
  feature.properties.$landType === 'cropzone' ||
  (feature.properties.$landType === 'field' &&
    !feature.properties.cropzones.length);

export const buildCustomLabel = (
  properties,
  mapLabelConfig,
  fields,
  nameOnly
) => {
  const customLabel = [];
  if (nameOnly) {
    return properties.name;
  }

  let parentField = properties;
  const isCropZone = properties.$landType === 'cropzone';
  if (isCropZone && properties.$parentFieldId) {
    parentField =
      fields instanceof Map
        ? fields.get(properties.$parentFieldId)
        : fields.find(field => field.id === properties.$parentFieldId);
  }

  if (mapLabelConfig.farmNames) {
    customLabel.push(parentField?.farmName);
  }
  if (mapLabelConfig.fieldNames) {
    customLabel.push(parentField?.name);
  }
  if (mapLabelConfig.cropZoneNames && isCropZone) {
    customLabel.push(properties.name);
  }
  if (mapLabelConfig.assignedCropType && isCropZone && properties.crop) {
    customLabel.push(properties.crop.name);
  }
  if (
    mapLabelConfig.area &&
    (properties.reportedArea || properties.boundaryArea)
  ) {
    customLabel.push(
      formatNumber(properties.reportedArea || properties.boundaryArea, 'ac')
    );
  }
  return customLabel.join('\n');
};
